import { useEffect, useState, useCallback } from "react";
import { useContextAwareGetProjects } from "@hooks/index";
import { FastProject } from "@typedefs/interfaces";
import { Option } from "@typedefs/modulesInterfaces";
import type { GetProjectsPayload } from "@app/features/project/projectTypes";

interface UsePaginatedProjectsOptions extends Omit<GetProjectsPayload, 'url'> {
  // Additional options can be added here
  refetchOnMountOrArgChange?: boolean;
}

interface UsePaginatedProjectsReturn {
  projectOptions: Option[];
  projects: FastProject[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  handleLoadMore: () => void;
  reset: () => void;
}

/**
 * Custom hook for handling paginated project fetching with load more functionality
 * @param options Configuration options for the projects query
 * @returns Object containing projects data, loading states, and pagination controls
 */
export const usePaginatedProjects = (
  options: UsePaginatedProjectsOptions = {}
): UsePaginatedProjectsReturn => {
  const [projects, setProjects] = useState<FastProject[]>([]);
  const [projectOptions, setProjectOptions] = useState<Option[]>([]);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | undefined>(undefined);

  const { refetchOnMountOrArgChange, ...queryOptions } = options;

  const { data: projectsResponse, isLoading } = useContextAwareGetProjects(
    { 
      fast: true, 
      url: currentUrl,
      ...queryOptions
    },
    { 
      refetchOnMountOrArgChange: refetchOnMountOrArgChange || false 
    }
  );

  useEffect(() => {
    const fastProjects = projectsResponse?.results as FastProject[];
    if (fastProjects) {
      if (currentUrl) {
        // This is a pagination request - append to existing data, avoiding duplicates
        setProjects(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newProjects = fastProjects.filter(project => !existingIds.has(project.id));
          return [...prev, ...newProjects];
        });
        setProjectOptions(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newOptions = fastProjects
            .filter(project => !existingIds.has(project.id))
            .map((project) => ({ 
              name: project.name, 
              id: project.id 
            } as Option));
          return [...prev, ...newOptions];
        });
        setIsLoadingMore(false);
      } else {
        // This is the initial request - replace data, but still ensure uniqueness
        const uniqueProjects = fastProjects.filter((project, index, arr) => 
          arr.findIndex(p => p.id === project.id) === index
        );
        setProjects(uniqueProjects);
        setProjectOptions(uniqueProjects.map((project) => ({ 
          name: project.name, 
          id: project.id 
        } as Option)));
      }
      setNextPageUrl(projectsResponse?.next || null);
    }
  }, [projectsResponse, currentUrl]);

  const handleLoadMore = useCallback(() => {
    if (!nextPageUrl || isLoadingMore || isLoading) return;
    
    setIsLoadingMore(true);
    try {
      // Set the next page URL to trigger a new query
      setCurrentUrl(nextPageUrl);
    } catch (error) {
      console.error('Error loading more projects:', error);
      setIsLoadingMore(false);
    }
  }, [nextPageUrl, isLoadingMore, isLoading]);

  const reset = useCallback(() => {
    setProjects([]);
    setProjectOptions([]);
    setNextPageUrl(null);
    setIsLoadingMore(false);
    setCurrentUrl(undefined);
  }, []);

  return {
    projectOptions,
    projects,
    isLoading,
    isLoadingMore,
    hasNextPage: Boolean(nextPageUrl),
    handleLoadMore,
    reset,
  };
}; 